import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/Layout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { CaretLeft, CaretRight, DotsSixVertical, Info, Plus, CalendarBlank, ListChecks, FileText, Briefcase, Check } from "@phosphor-icons/react";
import DocumentDialog from "@/components/DocumentDialog";
import EventDialog from "@/components/EventDialog";
import { toast } from "sonner";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const iso = (d) => d.toISOString().slice(0, 10);
const STATUS_ACCENT = {
  scheduled: "border-[#00B4D8] bg-cyan-50",
  in_progress: "border-amber-400 bg-amber-50",
  completed: "border-emerald-400 bg-emerald-50",
  unscheduled: "border-slate-300 bg-slate-50",
  cancelled: "border-rose-300 bg-rose-50",
};

export default function Schedule() {
  const [jobs, setJobs] = useState([]);
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [dragId, setDragId] = useState(null);
  const [overDay, setOverDay] = useState(null);
  const [docDialog, setDocDialog] = useState(null);   // { type: "job"|"quote" }
  const [eventDialog, setEventDialog] = useState(null); // { initial?, presetDate? }
  const lastDrag = React.useRef(0);
  const navigate = useNavigate();

  const load = () => api.listJobs({}).then(setJobs).catch(() => {});
  const loadEvents = () => api.listEvents().then(setEvents).catch(() => {});
  useEffect(() => { load(); loadEvents(); }, []);

  const { cells, monthLabel } = useMemo(() => {
    const year = cursor.getFullYear(), month = cursor.getMonth();
    const first = new Date(year, month, 1);
    const start = new Date(first);
    start.setDate(first.getDate() - first.getDay());
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      cells.push(d);
    }
    return { cells, monthLabel: cursor.toLocaleString("en-US", { month: "long", year: "numeric" }) };
  }, [cursor]);

  const jobsByDate = useMemo(() => {
    const map = {};
    jobs.filter((j) => j.scheduled_date).forEach((j) => { (map[j.scheduled_date] ||= []).push(j); });
    return map;
  }, [jobs]);

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((e) => { (map[e.date] ||= []).push(e); });
    return map;
  }, [events]);

  const toggleTask = async (ev, e) => {
    e.stopPropagation();
    setEvents((prev) => prev.map((x) => (x.id === ev.id ? { ...x, done: !x.done } : x)));
    try { await api.updateEvent(ev.id, { done: !ev.done }); }
    catch { loadEvents(); }
  };

  const unscheduled = useMemo(() => jobs.filter((j) => !j.scheduled_date && j.status !== "cancelled" && j.status !== "completed"), [jobs]);

  const todayIso = iso(new Date());
  const move = (n) => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + n, 1));

  const reschedule = async (jobId, newDate) => {
    const job = jobs.find((j) => j.id === jobId);
    if (!job || job.scheduled_date === newDate) return;
    // optimistic
    setJobs((prev) => prev.map((j) => (j.id === jobId ? { ...j, scheduled_date: newDate, status: j.status === "unscheduled" ? "scheduled" : j.status } : j)));
    try {
      await api.setJobSchedule(jobId, { scheduled_date: newDate });
      toast.success(`${job.number} moved to ${newDate}`);
    } catch (e) {
      toast.error("Couldn't reschedule");
      load();
    }
  };

  const onDrop = (dISO) => (e) => {
    e.preventDefault();
    setOverDay(null);
    const id = e.dataTransfer.getData("text/plain") || dragId;
    if (id) reschedule(id, dISO);
    setDragId(null);
  };

  const JobBlock = ({ j, compact }) => (
    <div
      draggable
      onDragStart={(e) => { e.dataTransfer.setData("text/plain", j.id); e.dataTransfer.effectAllowed = "move"; setDragId(j.id); }}
      onDragEnd={() => { lastDrag.current = Date.now(); setDragId(null); setOverDay(null); }}
      onClick={() => { if (Date.now() - lastDrag.current < 250) return; navigate(`/jobs/${j.id}`); }}
      data-testid={`cal-job-${j.id}`}
      className={`group text-[11px] leading-tight border-l-2 rounded px-1.5 py-1 cursor-pointer flex items-start gap-1 transition-shadow duration-200 hover:shadow-md ${STATUS_ACCENT[j.status] || STATUS_ACCENT.scheduled} ${dragId === j.id ? "opacity-40" : ""}`}
      title={`${j.title} · ${j.client_name} — drag to move, click to open`}
    >
      <DotsSixVertical size={12} className="text-slate-400 mt-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
      <span className="truncate">
        {j.scheduled_time && <span className="font-semibold text-slate-700">{j.scheduled_time} </span>}
        {j.title}
      </span>
    </div>
  );

  const EventBlock = ({ e }) => {
    const isTask = e.kind === "task";
    return (
      <div
        onClick={() => setEventDialog({ initial: e })}
        data-testid={`cal-event-${e.id}`}
        className={`group text-[11px] leading-tight border-l-2 rounded px-1.5 py-1 cursor-pointer flex items-start gap-1 transition-shadow duration-200 hover:shadow-md ${isTask ? "border-amber-400 bg-amber-50" : "border-violet-400 bg-violet-50"}`}
        title={e.title}
      >
        {isTask ? (
          <button onClick={(ev) => toggleTask(e, ev)} data-testid={`cal-task-toggle-${e.id}`}
            className={`mt-[1px] shrink-0 w-3.5 h-3.5 rounded-[3px] border flex items-center justify-center transition-colors ${e.done ? "bg-amber-500 border-amber-500" : "border-slate-400 bg-white hover:border-amber-500"}`}>
            {e.done && <Check size={10} weight="bold" className="text-white" />}
          </button>
        ) : (
          <CalendarBlank size={12} weight="duotone" className="text-violet-500 mt-[1px] shrink-0" />
        )}
        <span className={`truncate ${e.done ? "line-through text-slate-400" : ""}`}>
          {e.time && <span className="font-semibold text-slate-700">{e.time} </span>}
          {e.title}
        </span>
      </div>
    );
  };

  return (
    <div className="p-8 max-w-[1500px]">
      <PageHeader title="Schedule" subtitle="Drag jobs to reschedule · click to open" actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => move(-1)} data-testid="cal-prev"><CaretLeft size={18} /></Button>
          <div className="font-display font-semibold text-slate-900 w-44 text-center">{monthLabel}</div>
          <Button variant="outline" size="icon" onClick={() => move(1)} data-testid="cal-next"><CaretRight size={18} /></Button>
          <Button variant="outline" onClick={() => setCursor(new Date())}>Today</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="bg-[#00B4D8] hover:bg-cyan-600" data-testid="schedule-new-button"><Plus size={16} weight="bold" className="mr-1.5" /> New</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setEventDialog({})} data-testid="new-event-menu"><CalendarBlank size={16} className="mr-2 text-violet-500" /> Event or Task</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setDocDialog({ type: "job" })} data-testid="new-job-menu"><Briefcase size={16} className="mr-2 text-[#00B4D8]" /> Job</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setDocDialog({ type: "quote" })} data-testid="new-quote-menu"><FileText size={16} className="mr-2 text-[#00B4D8]" /> Quote</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      } />

      <div className="flex gap-6 items-start">
        {/* Calendar */}
        <div className="flex-1 bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
          <div className="grid grid-cols-7 bg-[#0B1726]">
            {DAYS.map((d) => <div key={d} className="text-center py-2.5 text-[11px] font-bold uppercase tracking-widest text-slate-300">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((d, i) => {
              const dISO = iso(d);
              const inMonth = d.getMonth() === cursor.getMonth();
              const dayJobs = jobsByDate[dISO] || [];
              const dayEvents = eventsByDate[dISO] || [];
              const isOver = overDay === dISO;
              return (
                <div
                  key={i}
                  onDragOver={(e) => { if (!inMonth) return; e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (overDay !== dISO) setOverDay(dISO); }}
                  onDragLeave={(e) => { if (e.currentTarget === e.target) setOverDay(null); }}
                  onDrop={inMonth ? onDrop(dISO) : undefined}
                  className={`group/day relative min-h-[120px] border-b border-r border-slate-100 p-1.5 transition-colors duration-150 ${inMonth ? "bg-white" : "bg-slate-50/60"} ${isOver ? "bg-cyan-50 ring-2 ring-inset ring-[#00B4D8]" : ""}`}
                  data-testid={`cal-day-${dISO}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className={`text-xs font-semibold flex items-center justify-center w-6 h-6 rounded-full ${dISO === todayIso ? "bg-[#00B4D8] text-white" : inMonth ? "text-slate-700" : "text-slate-300"}`}>{d.getDate()}</div>
                    {inMonth && (
                      <button onClick={() => setEventDialog({ presetDate: dISO })} data-testid={`cal-add-${dISO}`}
                        className="opacity-0 group-hover/day:opacity-100 transition-opacity duration-150 text-slate-400 hover:text-[#00B4D8]" title="Add event or task">
                        <Plus size={14} weight="bold" />
                      </button>
                    )}
                  </div>
                  <div className="space-y-1">
                    {dayJobs.map((j) => <JobBlock key={j.id} j={j} />)}
                    {dayEvents.map((e) => <EventBlock key={e.id} e={e} />)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Unscheduled tray */}
        <div
          className={`w-64 shrink-0 bg-white border border-slate-200 rounded-lg shadow-sm p-4 transition-colors duration-150 ${overDay === "__unsched__" ? "ring-2 ring-orange-300" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setOverDay("__unsched__"); }}
          onDragLeave={(e) => { if (e.currentTarget === e.target) setOverDay(null); }}
          onDrop={(e) => { e.preventDefault(); setOverDay(null); const id = e.dataTransfer.getData("text/plain") || dragId; if (id) { setJobs((prev) => prev.map((j) => j.id === id ? { ...j, scheduled_date: null, status: "unscheduled" } : j)); api.setJobSchedule(id, { scheduled_date: null }).then(() => toast.success("Moved to unscheduled")).catch(() => load()); } setDragId(null); }}
          data-testid="unscheduled-tray"
        >
          <h3 className="font-display font-semibold text-slate-900 text-sm mb-1">Unscheduled</h3>
          <p className="text-[11px] text-slate-400 mb-3 flex items-center gap-1"><Info size={12} /> Drag onto a day to book</p>
          <div className="space-y-1.5">
            {unscheduled.length === 0 && <p className="text-xs text-slate-400">All jobs are scheduled.</p>}
            {unscheduled.map((j) => <JobBlock key={j.id} j={j} />)}
          </div>
          <div className="mt-4 pt-3 border-t border-slate-100 space-y-1.5 text-[11px] text-slate-500">
            <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm border-l-2 border-[#00B4D8] bg-cyan-50" /> Job</div>
            <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm border-l-2 border-violet-400 bg-violet-50" /> Event</div>
            <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm border-l-2 border-amber-400 bg-amber-50" /> Task</div>
          </div>
        </div>
      </div>

      <DocumentDialog
        open={!!docDialog} type={docDialog?.type} onClose={() => setDocDialog(null)}
        onSaved={() => { setDocDialog(null); load(); }}
      />
      <EventDialog
        open={!!eventDialog} initial={eventDialog?.initial} presetDate={eventDialog?.presetDate}
        onClose={() => setEventDialog(null)} onSaved={() => { setEventDialog(null); loadEvents(); }}
      />
    </div>
  );
}
